import { is_numeric, intval, strval, in_array, empty } from '../../php-compat';
import type { PortEnv } from '../../port-contract';
import { isArray, slot, values, type Arr } from './access';
import { skillRollStatusIdScalar, type StatusIdScalarDeps } from './skill_roll_status_trigger_gates';
export interface NextSpellCarrierDeps extends StatusIdScalarDeps {
    janus_trigger_in_progress(): boolean;
    creature_payload_statuses(caster: Arr): Arr;
    layer_has(row: Arr, name: string): boolean;
    layer_value(row: Arr, name: string): unknown;
    item_field(row: unknown, name: string): unknown;
}
/** Shared storage walk; each port retains its source's numeric acceptance rule. */
export function numericNextSpellCarriers(caster: Arr, layer: string, key: string, minimum: number,
    env: PortEnv<NextSpellCarrierDeps>, requireNumeric = true, checkTrigger = true): Arr {
    if (checkTrigger && env.deps.janus_trigger_in_progress()) return [];
    const carriers: Arr[] = [];
    for (const wrapper of values(env.deps.creature_payload_statuses(caster))) {
        if (!isArray(wrapper)) continue;
        const status = slot(wrapper, 'status_arr');
        if (!isArray(status) || !env.deps.layer_has(status, layer)) continue;
        const raw = env.deps.layer_value(status, layer), numeric = is_numeric(raw), value = numeric || !requireNumeric ? Number(intval(raw)) : 0;
        const id = skillRollStatusIdScalar(slot(wrapper, 'status_id'), env);
        if (requireNumeric && !numeric || value < minimum || id === '') continue;
        carriers.push({ status_id: id,
            status_item_id: skillRollStatusIdScalar(slot(wrapper, 'status_item_id') ?? env.deps.item_field(status, 'item_id'), env),
            [key]: value, name: strval(env.deps.item_field(wrapper, 'item_name') ?? 'Status Effect') });
    }
    return carriers;
}
export function flagNextSpellCarriers(caster: Arr, layer: string, env: PortEnv<NextSpellCarrierDeps>): Arr {
    if (env.deps.janus_trigger_in_progress()) return [];
    const carriers: Arr[] = [];
    for (const wrapper of values(env.deps.creature_payload_statuses(caster))) {
        if (!isArray(wrapper)) continue;
        const status = slot(wrapper, 'status_arr');
        if (!isArray(status) || empty(env.deps.layer_value(status, layer))) continue;
        const id = skillRollStatusIdScalar(slot(wrapper, 'status_id'), env);
        if (id === '') continue;
        carriers.push({ status_id: id,
            status_item_id: skillRollStatusIdScalar(slot(wrapper, 'status_item_id') ?? env.deps.item_field(status, 'item_id'), env),
            name: strval(env.deps.item_field(wrapper, 'item_name') ?? 'Status Effect') });
    }
    return carriers;
}
export function isSlowSpell(ability: Arr, env: PortEnv<NextSpellCarrierDeps>): boolean {
    return in_array(strval(env.deps.layer_value(ability, 'spell_casting_time') ?? ''),
        ['one_minute', 'ten_minutes', 'one_hour', 'eight_hours', 'twelve_hours', 'twenty_four_hours'], true);
}
export function isFastNextSpell(ability: Arr | null, variable: unknown, env: PortEnv<NextSpellCarrierDeps>): boolean {
    if (ability === null || env.deps.item_field(ability, 'item_type') !== 'spell') return false;
    if (in_array(variable, ['spell_ritual', 'finish_casting', 'finish_casting_ritual_spell'], true)) return false;
    const time = strval(env.deps.layer_value(ability, 'spell_casting_time') ?? '');
    return !in_array(time, ['one_minute', 'ten_minutes', 'one_hour', 'eight_hours', 'twelve_hours', 'twenty_four_hours'], true);
}
